import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import AdminHeader from '../components/AdminHeader';
import Toast from '../components/Toast';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { API_BASE_URL } from '../config/api';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper function to make authenticated requests
const authAxios = {
  get: (url, config = {}) => axios.get(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  post: (url, data, config = {}) => axios.post(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  put: (url, data, config = {}) => axios.put(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } })
};

function OrderDetailsPage() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fulfilling, setFulfilling] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [resendingDownloads, setResendingDownloads] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [sendEmailOnStatusChange, setSendEmailOnStatusChange] = useState(true);
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  useEffect(() => {
    if (orderId) {
      document.title = `Order ${orderId} - Design Store Admin`;
      fetchOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchOrder runs when orderId changes
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAxios.get(`${API_BASE_URL}/orders/${orderId}`);
      if (response.data.success) {
        setOrder(response.data.data);
      } else {
        setError('Failed to fetch order');
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      setError(err.response?.data?.error || 'Failed to fetch order');
    } finally {
      setLoading(false);
    }
  };

  const handleFulfillOrder = async () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Fulfill Order',
      message: 'Mark this order as fulfilled and send "order is on the way" email to customer?',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          setFulfilling(true);
          const response = await authAxios.post(`${API_BASE_URL}/orders/${orderId}/fulfill`);
          if (response.data.success) {
            setToast({ message: response.data.message || 'Order marked as fulfilled', type: 'success' });
            await fetchOrder(); // Refresh order
          } else {
            setToast({ message: 'Failed to fulfill order: ' + (response.data.error || 'Unknown error'), type: 'error' });
          }
        } catch (err) {
          console.error('Error fulfilling order:', err);
          setToast({ message: 'Failed to fulfill order: ' + (err.response?.data?.error || 'Unknown error'), type: 'error' });
        } finally {
          setFulfilling(false);
        }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleSendEmail = async () => {
    if (!emailSubject || !emailMessage) {
      setToast({ message: 'Please enter both subject and message', type: 'warning' });
      return;
    }

    try {
      setSendingEmail(true);
      const response = await authAxios.post(`${API_BASE_URL}/orders/${orderId}/send-email`, {
        subject: emailSubject,
        message: emailMessage
      });
      if (response.data.success) {
        setToast({ message: 'Email sent successfully!', type: 'success' });
        setShowEmailModal(false);
        setEmailSubject('');
        setEmailMessage('');
      } else {
        setToast({ message: 'Failed to send email: ' + (response.data.error || 'Unknown error'), type: 'error' });
      }
    } catch (err) {
      console.error('Error sending email:', err);
      setToast({ message: 'Failed to send email: ' + (err.response?.data?.error || 'Unknown error'), type: 'error' });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleResendDownloads = async () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Resend Download Links',
      message: 'Send download links email to the customer?',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          setResendingDownloads(true);
          const response = await authAxios.post(`${API_BASE_URL}/orders/${orderId}/resend-downloads`);
          if (response.data.success) {
            setToast({ message: response.data.message || 'Download links email sent successfully!', type: 'success' });
            await fetchOrder(); // Refresh order to get updated download links if they were generated
          } else {
            setToast({ message: 'Failed to resend downloads: ' + (response.data.error || 'Unknown error'), type: 'error' });
          }
        } catch (err) {
          console.error('Error resending downloads:', err);
          setToast({ message: 'Failed to resend downloads: ' + (err.response?.data?.error || 'Unknown error'), type: 'error' });
        } finally {
          setResendingDownloads(false);
        }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleStatusChange = async (newStatus) => {
    const emailMessage = sendEmailOnStatusChange 
      ? ' An email will be sent to the customer.' 
      : ' No email will be sent.';
    setConfirmDialog({
      isOpen: true,
      title: 'Change Order Status',
      message: `Change order status from "${order.status}" to "${newStatus}"?${emailMessage}`,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          setChangingStatus(true);
          const response = await authAxios.put(`${API_BASE_URL}/orders/${orderId}/status`, {
            status: newStatus,
            sendEmail: sendEmailOnStatusChange
          });
          if (response.data.success) {
            setToast({ message: response.data.message || 'Status updated successfully', type: 'success' });
            await fetchOrder(); // Refresh order
          } else {
            setToast({ message: 'Failed to update status: ' + (response.data.error || 'Unknown error'), type: 'error' });
          }
        } catch (err) {
          console.error('Error updating status:', err);
          setToast({ message: 'Failed to update status: ' + (err.response?.data?.error || 'Unknown error'), type: 'error' });
        } finally {
          setChangingStatus(false);
        }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'fulfilled':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <AdminHeader />
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading order...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen py-8 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <AdminHeader />
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error || 'Order not found'}</p>
            <button
              onClick={() => navigate('/admin/orders')}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Orders
            </button>
          </div>
        </div>
      </div>
    );
  }

  const hasDigitalItems = order.items && order.items.some(item => item.type === 'Digital');
  const hasPhysicalItems = order.items && order.items.some(item => item.type !== 'Digital');

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <AdminHeader />

        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/orders')}
            className="text-blue-600 hover:text-blue-800 mb-4 inline-flex items-center"
          >
            ← Back to Orders
          </button>
          <h2 className="text-2xl font-bold text-gray-900">Order Details</h2>
        </div>

        {/* Order Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Order {order.id}</h3>
              <p className="text-sm text-gray-600">Placed on {formatDate(order.date)}</p>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                  <select
                    value={order.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={changingStatus}
                    className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="fulfilled">Fulfilled</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  {changingStatus && (
                    <span className="text-sm text-gray-600">Updating...</span>
                  )}
                </div>
                {order.customer?.email && (
                  <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendEmailOnStatusChange}
                      onChange={(e) => setSendEmailOnStatusChange(e.target.checked)}
                      disabled={changingStatus}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                    />
                    <span>Send email update to customer</span>
                  </label>
                )}
              </div>
              {order.status !== 'fulfilled' && order.status !== 'cancelled' && hasPhysicalItems && (
                <button
                  onClick={handleFulfillOrder}
                  disabled={fulfilling}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {fulfilling ? 'Processing...' : 'Mark as Fulfilled'}
                </button>
              )}
              {order.customer?.email && (
                <button
                  onClick={() => {
                    setEmailSubject(`Update on your order ${order.id}`);
                    setShowEmailModal(true);
                  }}
                  disabled={sendingEmail}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Send Email
                </button>
              )}
              {hasDigitalItems && order.customer?.email && (
                <button
                  onClick={handleResendDownloads}
                  disabled={resendingDownloads}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {resendingDownloads ? 'Sending...' : 'Resend Download Links'}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Customer Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium text-gray-600">Name:</span>
                <p className="text-gray-900">{order.customer?.name || 'N/A'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Email:</span>
                <p className="text-gray-900">{order.customer?.email || 'N/A'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Phone:</span>
                <p className="text-gray-900">{order.customer?.phone || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Shipping Information */}
          {order.shipping && order.shipping.address && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Shipping Address</h3>
              <div className="space-y-2">
                <p className="text-gray-900">{order.shipping.name}</p>
                <p className="text-gray-900">{order.shipping.address}</p>
                <p className="text-gray-900">
                  {order.shipping.city}, {order.shipping.state} {order.shipping.zip}
                </p>
                <p className="text-gray-900">{order.shipping.country}</p>
              </div>
            </div>
          )}
        </div>

        {/* Order Items */}
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Design
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Format
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {order.items?.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.designTitle || item.designId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.formatName || item.formatId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        item.type === 'Digital' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {item.type || 'Physical'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.quantity || 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${(item.price || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ${((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Digital Downloads */}
        {hasDigitalItems && order.digitalDownloads && order.digitalDownloads.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Digital Downloads</h3>
            <div className="space-y-4">
              {order.digitalDownloads.map((download, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">
                        {download.designTitle} - {download.formatName}
                      </p>
                      <p className="text-sm text-gray-600">Quantity: {download.quantity}</p>
                      <p className="text-sm text-green-600 font-medium mt-2">
                        Status: Completed
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        Download clicks: {download.clickCount || 0}
                      </p>
                    </div>
                    <a
                      href={download.downloadLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      View Download Link
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Order Totals */}
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal:</span>
              <span className="text-gray-900">${order.totals?.subtotal?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Shipping:</span>
              <span className="text-gray-900">
                {order.totals?.freeShipping ? 'FREE' : `$${order.totals?.shipping?.toFixed(2) || '0.00'}`}
              </span>
            </div>
            <div className="flex justify-between text-lg font-semibold pt-2 border-t border-gray-200">
              <span className="text-gray-900">Total:</span>
              <span className="text-gray-900">${order.totals?.total?.toFixed(2) || '0.00'}</span>
            </div>
          </div>
        </div>

        {/* Payment Information */}
        {order.payment && (
          <div className="bg-white rounded-lg shadow-md p-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium text-gray-600">Method:</span>
                <p className="text-gray-900">{order.payment.method || 'N/A'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Status:</span>
                <p className="text-gray-900">{order.payment.status || 'N/A'}</p>
              </div>
              {order.payment.paymentIntentId && (
                <div>
                  <span className="text-sm font-medium text-gray-600">Payment Intent ID:</span>
                  <p className="text-gray-900 text-sm font-mono">{order.payment.paymentIntentId}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        {order.notes && (
          <div className="bg-white rounded-lg shadow-md p-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
            <p className="text-gray-900 whitespace-pre-wrap">{order.notes}</p>
          </div>
        )}

        {/* Email Modal */}
        {showEmailModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Send Email to Customer</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject:
                  </label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Email subject"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message:
                  </label>
                  <textarea
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Email message"
                  />
                </div>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowEmailModal(false);
                      setEmailSubject('');
                      setEmailMessage('');
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendEmail}
                    disabled={sendingEmail || !emailSubject || !emailMessage}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {sendingEmail ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Toast Notification */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
        
        {/* Confirmation Dialog */}
        {confirmDialog && (
          <ConfirmationDialog
            isOpen={confirmDialog.isOpen}
            title={confirmDialog.title}
            message={confirmDialog.message}
            onConfirm={confirmDialog.onConfirm}
            onCancel={confirmDialog.onCancel}
          />
        )}
      </div>
    </div>
  );
}

export default OrderDetailsPage;

